(function () {
  'use strict';

  if (customElements.get('strip-pdp')) return;

  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(Number(cents) / 100);

  const cartAddUrl = (url) => {
    const base = url || '/cart/add';
    return base.endsWith('.js') ? base : base + '.js';
  };

  const openCart = (message) => {
    if (!window.Wisp) return;
    if (message && typeof window.Wisp.showToast === 'function') window.Wisp.showToast(message);
    if (typeof window.Wisp.openCart === 'function') window.Wisp.openCart();
  };

  class StripPdp extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.name = this.dataset.productName || 'FUSE';
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.quantityOptions = Array.from(this.querySelectorAll('[data-quantity-option]'));
      this.purchaseOptions = Array.from(this.querySelectorAll('[data-purchase-option]'));
      this.plans = Array.from(this.querySelectorAll('[data-sp-plan]'));
      this.subscriptionOption = this.querySelector('[data-subscription-option]');
      this.onceOption = this.purchaseOptions.find((option) => option !== this.subscriptionOption);
      this.subToggle = this.querySelector('[data-sub-toggle]');
      this.addon = this.querySelector('[data-addon]');
      this.form = this.querySelector('[data-sp-form]');
      this.status = this.querySelector('[data-form-status]');
      this.addButton = this.querySelector('[data-add-button]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.activeSlide = 0;

      this.bindGallery();
      this.bindLightbox();
      this.bindOptions();
      this.bindForm();
      this.applyPackIntent();
      if (this.subscriptionOption && this.subToggle?.checked && this.planFor(this.selectedQuantity())) {
        this.subscriptionOption.checked = true;
      }
      this.updatePurchasePrices();
      this.dispatchEvent(new CustomEvent('pdp:ready', { bubbles: true }));
    }

    /* ---------- gallery ---------- */

    bindGallery() {
      if (this.slides.length < 2) {
        const controls = this.querySelector('[data-gallery-controls]');
        if (controls) controls.hidden = true;
      }
      this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.showSlide(this.activeSlide - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showSlide(this.activeSlide + 1));
      this.querySelector('[data-gallery-retry]')?.addEventListener('click', () => {
        if (this.viewerLoadFailed) this.openLightbox();
        else if (Number.isInteger(this.failedSlide)) this.showSlide(this.failedSlide);
      });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.galleryThumb)));
      });
      this.querySelectorAll('[data-show-facts]').forEach((button) => {
        button.addEventListener('click', () => this.showFacts());
      });
      document.addEventListener('strip-pdp:show-facts', () => this.showFacts());

      const stage = this.querySelector('[data-gallery-stage]');
      let start = null;
      stage?.addEventListener('touchstart', (event) => { start = event.touches.length === 1 ? event.touches[0] : null; }, { passive: true });
      stage?.addEventListener('touchcancel', () => { start = null; });
      stage?.addEventListener('touchend', (event) => {
        if (!start) return;
        const dx = event.changedTouches[0].clientX - start.clientX;
        const dy = event.changedTouches[0].clientY - start.clientY;
        start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.showSlide(this.activeSlide + (dx < 0 ? 1 : -1));
      });
    }

    async showFacts() {
      const facts = this.slides.findIndex((slide) => slide.hasAttribute('data-facts-slide'));
      if (!await this.showSlide(facts >= 0 ? facts : this.slides.length - 1)) return;
      if (this.mobileQuery.matches) {
        this.openLightbox();
      } else {
        this.querySelector('[data-gallery-stage]')?.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }

    async showSlide(index) {
      if (!this.slides.length) return false;
      const nextSlide = (index + this.slides.length) % this.slides.length;
      const request = (this.slideRequest || 0) + 1;
      const incomingImage = this.slides[nextSlide]?.querySelector('img');
      const stage = this.querySelector('[data-gallery-stage]');
      const feedback = this.querySelector('[data-gallery-error]');
      const retryButton = this.querySelector('[data-gallery-retry]');
      const lightboxRetry = this.querySelector('[data-lightbox-retry]');
      this.slideRequest = request;

      if (incomingImage && (!incomingImage.complete || !incomingImage.naturalWidth)) {
        stage?.setAttribute('aria-busy', 'true');
        if (retryButton) retryButton.disabled = true;
        if (this.lightbox?.open && lightboxRetry) lightboxRetry.disabled = true;
        incomingImage.loading = 'eager';
        // Reassigning a failed URL starts a fresh request; a successful slide stays visible meanwhile.
        if (this.failedSlide === nextSlide) incomingImage.src = incomingImage.getAttribute('src');
        let timer;
        try {
          await Promise.race([
            incomingImage.decode(),
            new Promise((_, reject) => { timer = window.setTimeout(() => reject(new Error('Image timed out')), 10000); })
          ]);
          if (!incomingImage.naturalWidth) throw new Error('Image unavailable');
        } catch (error) {
          if (request !== this.slideRequest) return false;
          this.failedSlide = nextSlide;
          if (feedback) {
            feedback.hidden = false;
            feedback.querySelector('[data-gallery-error-text]').textContent = 'This image couldn’t load. Your previous image is still here.';
          }
          const status = this.querySelector('[data-gallery-status]');
          if (status) status.textContent = 'This image could not load. Your previous image is still shown. Try the image again.';
          if (this.lightbox?.open) this.viewer?.showError('This image couldn’t load. Your previous image is still shown.');
          return false;
        } finally {
          window.clearTimeout(timer);
          if (request === this.slideRequest) {
            stage?.removeAttribute('aria-busy');
            if (retryButton) retryButton.disabled = false;
            if (lightboxRetry) lightboxRetry.disabled = false;
          }
        }
        if (request !== this.slideRequest) return false;
      }

      this.failedSlide = null;
      if (feedback) feedback.hidden = true;
      if (retryButton) retryButton.disabled = false;
      stage?.removeAttribute('aria-busy');
      this.activeSlide = nextSlide;
      this.slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== nextSlide; });
      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === nextSlide;
        thumb.setAttribute('aria-pressed', String(active));
        if (active && this.mobileQuery.matches) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
      const position = nextSlide + 1;
      const status = this.querySelector('[data-gallery-status]');
      if (status) status.textContent = 'Image ' + position + ' of ' + this.slides.length;
      this.querySelector('[data-gallery-lightbox-trigger]')?.setAttribute('aria-label', 'Open image ' + position + ' of ' + this.slides.length + ' fullscreen');
      if (this.lightbox?.open) this.viewer?.updateImage();
      return true;
    }

    bindLightbox() {
      const stage = this.querySelector('[data-gallery-stage]');
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      if (!stage || !trigger || !this.lightbox || typeof this.lightbox.showModal !== 'function') {
        if (trigger) trigger.hidden = true;
        return;
      }
      stage.addEventListener('click', (event) => {
        if (!event.target.closest('button') && event.target.closest('[data-gallery-slide]')) this.openLightbox();
      });
      trigger.addEventListener('click', () => this.openLightbox());
    }

    async openLightbox() {
      if ((!this.mobileQuery.matches && !this.matches('.sp--boost')) || !this.lightbox || this.lightbox.open) return;
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      trigger?.setAttribute('aria-busy', 'true');
      try {
        if (!this.viewerPromise) {
          const url = new URL(this.dataset.viewerUrl, document.baseURI);
          if (this.viewerRetries) url.searchParams.set('viewer_retry', this.viewerRetries);
          this.viewerPromise = import(url.href).then(({StripPdpViewer}) => this.viewer = new StripPdpViewer(this));
        }
        const viewer = await this.viewerPromise;
        this.viewerLoadFailed = false;
        const feedback = this.querySelector('[data-gallery-error]');
        if (feedback) feedback.hidden = true;
        viewer.open();
      } catch (error) {
        this.viewerPromise = null;
        this.viewerRetries = (this.viewerRetries || 0) + 1;
        this.viewerLoadFailed = true;
        const feedback = this.querySelector('[data-gallery-error]');
        if (feedback) {
          feedback.hidden = false;
          feedback.querySelector('[data-gallery-error-text]').textContent = 'The fullscreen viewer couldn’t open. Please try again.';
        }
        const status = this.querySelector('[data-gallery-status]');
        if (status) status.textContent = 'The fullscreen viewer couldn’t open. Please try again.';
      } finally {
        trigger?.removeAttribute('aria-busy');
      }
    }

    /* ---------- purchase box ---------- */

    bindOptions() {
      this.quantityOptions.forEach((option) => option.addEventListener('change', () => this.updatePurchasePrices()));
      this.purchaseOptions.forEach((option) => option.addEventListener('change', () => this.updatePurchasePrices()));
      this.addon?.addEventListener('change', () => this.updatePurchasePrices());
      this.subToggle?.addEventListener('change', () => {
        const target = this.subToggle.checked ? this.subscriptionOption : this.onceOption;
        if (target && !target.disabled) target.checked = true;
        this.updatePurchasePrices();
      });
    }

    selectedQuantity() {
      const selected = this.quantityOptions.find((option) => option.checked);
      return selected ? Number(selected.value) : 1;
    }

    planFor(quantity) {
      return this.plans.find((plan) => Number(plan.dataset.quantity) === quantity);
    }

    applyPackIntent() {
      const pack = Number(new URL(window.location.href).searchParams.get('pack'));
      if (![1, 2, 3].includes(pack)) return;
      const option = this.quantityOptions.find((candidate) => Number(candidate.value) === pack);
      if (option) option.checked = true;
    }

    updatePurchasePrices() {
      const currency = this.dataset.currency;
      const quantity = this.selectedQuantity();
      const quantityOption = this.quantityOptions.find((option) => option.checked);
      if (!quantityOption) return;
      const onceTotal = Number(quantityOption.dataset.onceTotal);
      const plan = this.planFor(quantity);
      const subscriptionOption = this.subscriptionOption;
      const setText = (selector, value) => {
        this.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
      };

      if (subscriptionOption) {
        subscriptionOption.disabled = !plan;
        if (!plan && subscriptionOption.checked && this.onceOption) this.onceOption.checked = true;
        subscriptionOption.value = plan?.dataset.id || '';
        subscriptionOption.dataset.sellingPlanId = plan?.dataset.id || '';
        subscriptionOption.dataset.planName = plan?.dataset.name || '';
        subscriptionOption.dataset.planCadence = plan?.dataset.cadence || '';
        subscriptionOption.dataset.planRenewalEstimated = plan?.dataset.renewalEstimated || 'false';
        this.plans.forEach((candidate) => {
          subscriptionOption.setAttribute('data-plan-total-' + candidate.dataset.quantity, candidate.dataset.total);
          subscriptionOption.setAttribute('data-plan-renewal-total-' + candidate.dataset.quantity, candidate.dataset.renewal);
        });
      }

      const subscribed = Boolean(subscriptionOption?.checked && plan);
      if (this.subToggle) {
        this.subToggle.checked = subscribed;
        this.subToggle.disabled = !plan;
        this.querySelector('[data-sub-card]')?.classList.toggle('is-unavailable', !plan);
      }

      if (plan && onceTotal > 0) {
        const firstPct = Math.max(0, Math.round((onceTotal - Number(plan.dataset.total)) * 100 / onceTotal));
        const renewPct = Math.max(0, Math.round((onceTotal - Number(plan.dataset.renewal)) * 100 / onceTotal));
        setText('[data-sub-first-pct]', String(firstPct));
        setText('[data-sub-renew-pct]', String(renewPct));
        setText('[data-sub-cadence]', plan.dataset.cadence);
        setText('[data-subscription-renewal-total]', money(plan.dataset.renewal, currency));
        setText('[data-subscription-cadence]', plan.dataset.cadence);
      }
      const terms = this.querySelector('[data-sub-terms]');
      if (terms) terms.hidden = !subscribed || this.matches('.sp--boost');
      setText('[data-once-price]', money(onceTotal, currency));
      setText('[data-sub-first-price]', plan ? money(plan.dataset.total, currency) : '');
      const show = (selector, visible) => this.querySelectorAll(selector).forEach((el) => { el.hidden = !visible; });
      show('[data-sub-offer], [data-sub-first-price]', !!plan);
      show('[data-sub-unavailable]', !plan);
      show('[data-shipping-free]', quantity >= 3);
      show('[data-shipping-paid]', quantity < 3);

      // BOOST compares packs with single tins bought the same way, so the subscription discount isn't counted twice.
      const singlePlan = this.matches('.sp--boost') ? this.planFor(1) : null;
      this.quantityOptions.forEach((option) => {
        const packQuantity = Number(option.value);
        const packPlan = this.planFor(packQuantity);
        const total = subscribed && packPlan ? Number(packPlan.dataset.total) : Number(option.dataset.onceTotal);
        const compare = subscribed && packPlan && singlePlan ? Number(singlePlan.dataset.total) * packQuantity : Number(option.dataset.onceCompare);
        const tile = option.closest('label');
        const totalElement = tile.querySelector('[data-quantity-total]');
        const unitElement = tile.querySelector('[data-quantity-unit-price]');
        const saving = tile.querySelector('[data-quantity-discount]');
        if (totalElement) totalElement.textContent = money(total, currency);
        if (unitElement) unitElement.textContent = money(Math.round(total / (packQuantity * 30)), currency);
        if (saving) {
          saving.hidden = compare <= total;
          saving.textContent = 'Save ' + money(compare - total, currency);
        }
      });

      const selectedTotal = subscribed ? Number(plan.dataset.total) : onceTotal;
      const addonTotal = this.addon?.checked ? Number(this.addon.dataset.addonPrice || 0) : 0;
      setText('[data-add-price]', money(selectedTotal + addonTotal, currency));
      this.dataset.selectedTotal = String(selectedTotal);

      // Keep the primary offer in the same update as the selected price, including section reloads.
      const offer = this.querySelector('[data-boost-offer]');
      if (offer) {
        setText('[data-boost-offer-price]', money(selectedTotal, currency));
        setText('[data-boost-offer-count]', (quantity * 30) + ' ' + offer.dataset.stripsLabel);
        setText('[data-boost-offer-unit]', money(Math.round(selectedTotal / (quantity * 30)), currency));
        setText('[data-boost-offer-mode]', subscribed ? offer.dataset.subscribeLabel : offer.dataset.onceLabel);
      }
      this.dispatchEvent(new CustomEvent('pdp:purchase-change', { bubbles: true }));
    }

    bindForm() {
      if (!this.form || !this.addButton || !window.fetch || !window.FormData) return;
      this.form.addEventListener('submit', (event) => this.addToCart(event));
    }

    lockOptions(locked) {
      this.quantityOptions.forEach((option) => { option.disabled = locked; });
      if (locked) this.purchaseOptions.forEach((option) => { option.disabled = true; });
      if (this.subToggle) this.subToggle.disabled = locked;
      if (this.addon) this.addon.disabled = locked;
      if (!locked) {
        this.purchaseOptions.forEach((option) => { option.disabled = false; });
      }
    }

    async addToCart(event) {
      event.preventDefault();
      if (!window.FuseCartQuantity) {
        this.form.submit();
        return;
      }
      if (this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;

      // Read the form before locking it: disabled radios are left out of FormData.
      const formData = new FormData(this.form);
      const withAddon = Boolean(this.addon?.checked);
      const addonName = this.addon?.dataset.addonName || '';
      const label = this.querySelector('[data-add-label]');
      const originalLabel = label ? label.textContent : '';
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
      this.lockOptions(true);
      if (label) label.textContent = 'ADDING...';
      this.setStatus('Adding ' + this.name + ' to your cart.', 'status');

      let mainAdded = false;
      try {
        await window.FuseCartQuantity.add(cartAddUrl(this.dataset.cartAddUrl), formData);
        mainAdded = true;
        if (withAddon) {
          // A separate one-time line preserves the main item's subscription tier.
          const addonData = new FormData();
          addonData.append('id', this.addon.dataset.variantId);
          addonData.append('quantity', '1');
          await window.FuseCartQuantity.add(cartAddUrl(this.dataset.cartAddUrl), addonData);
        }
        const added = withAddon ? this.name + ' and ' + addonName : this.name;
        this.setStatus(added + ' added to your cart.', 'success');
        if (label) label.textContent = 'ADDED';
        if (this.addon) this.addon.checked = false;
        openCart(added + ' added to cart');
      } catch (error) {
        if (error.addConfirmed || mainAdded) {
          const message = mainAdded && withAddon && !error.addConfirmed
            ? this.name + ' was added, but ' + addonName + ' could not be added. Try adding it again.'
            : this.name + ' added. Open your cart to finish updating subscription pricing.';
          this.setStatus(message, 'status');
          if (label) label.textContent = 'ADDED';
          window.Wisp?.openCart?.();
          return;
        }
        const message = error.transportError ? 'The cart request could not be confirmed. Check your cart before retrying.' : error.message;
        this.setStatus(message, 'error');
        if (label) label.textContent = 'TRY AGAIN';
        if (typeof window.Wisp?.showToast === 'function') window.Wisp.showToast(message);
      } finally {
        window.setTimeout(() => {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.disabled = this.addButton.dataset.productAvailable === 'false';
          this.lockOptions(false);
          this.updatePurchasePrices();
          if (label) label.textContent = originalLabel;
        }, 1200);
      }
    }

    setStatus(message, state) {
      if (this.status) {
        this.status.textContent = message;
        this.status.dataset.state = state;
      }
      this.dispatchEvent(new CustomEvent('pdp:cart-status', { detail: { message, state } }));
    }
  }

  customElements.define('strip-pdp', StripPdp);

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-sp-show-facts]')) document.dispatchEvent(new CustomEvent('strip-pdp:show-facts'));
  });
})();
